import { supabase } from '../../client';
import { Item } from '@/types/purchase';
import { PRODUCT_CATALOG_LIMIT } from '@/constants/catalog';
import { logError } from '@/lib/sentry';

const TABLE_NAME = 'product_catalog';

/**
 * 사용자의 전체 상품 카탈로그를 데이터베이스에서 조회합니다.
 * last_used_at을 기준으로 최신순으로 정렬하여 상위 500개만 반환합니다.
 * @param userId - 조회할 사용자의 ID
 * @returns 상품 아이템 배열
 */
export async function getProductCatalog(userId: string): Promise<Item[]> {
  if (!userId) return [];

  const { data, error } = await supabase
    .from(TABLE_NAME)
    .select('name, price') // UI에 필요한 필드만 선택
    .eq('user_id', userId)
    .order('last_used_at', { ascending: false, nullsFirst: false })
    .limit(PRODUCT_CATALOG_LIMIT);

  if (error) {
    logError('Failed to fetch product catalog from DB', error);
    throw error;
  }

  // DB 스키마와 Item 타입 간의 불일치를 맞추기 위해 quantity: 1을 추가합니다.
  return data.map(item => ({ ...item, quantity: 1 }));
}

/**
 * 상품 카탈로그에 아이템을 추가하거나 업데이트(Upsert)합니다.
 * 동일한 이름의 상품이 존재하면 가격과 last_used_at을 업데이트하고,
 * 존재하지 않으면 새로운 상품으로 추가합니다.
 * @param userId - 사용자 ID
 * @param item - 추가 또는 업데이트할 상품 아이템
 */
export async function upsertProductCatalogItem(userId: string, item: Item): Promise<void> {
  if (!userId) return;

  const { error } = await supabase
    .from(TABLE_NAME)
    .upsert(
      {
        user_id: userId,
        name: item.name,
        price: item.price,
        last_used_at: new Date().toISOString(),
      },
      {
        onConflict: 'user_id, name',
      }
    );

  if (error) {
    logError('Failed to upsert product catalog item to DB', error);
    throw error;
  }
}

/**
 * 상품 카탈로그에서 특정 아이템을 삭제합니다.
 * @param userId - 사용자 ID
 * @param itemName - 삭제할 상품의 이름
 */
export async function deleteProductCatalogItem(userId: string, itemName: string): Promise<void> {
  if (!userId) return;

  const { error } = await supabase
    .from(TABLE_NAME)
    .delete()
    .eq('user_id', userId)
    .eq('name', itemName);

  if (error) {
    logError('Failed to delete product catalog item from DB', error);
    throw error;
  }
}

/**
 * 사용자의 전체 상품 카탈로그를 삭제합니다.
 * @param userId - 사용자 ID
 */
export async function clearProductCatalog(userId: string): Promise<void> {
  if (!userId) return;

  const { error } = await supabase
    .from(TABLE_NAME)
    .delete()
    .eq('user_id', userId);

  if (error) {
    logError('Failed to clear product catalog from DB', error);
    throw error;
  }
}